/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
	ISchemaConstArray,
	ISchemaConstArrayModel,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { FieldHeaderButton } from "../../Fields/FieldHeaderButton";
import { FIELD_ROW_TYPE, FieldRow } from "../../Fields/FieldRow";
import {
	FIELD_LIST_ITEM_ALIGN,
	FieldListItem,
	IFieldListItemAction,
	IFieldListItemProps
} from "../../Fields/FieldListItem";
import { InspectorFloatingPanel } from "../InspectorFloatingPanel";
import { InspectModel } from "../InspectModel";
import { DragAndDropContext, IDragAndDropContextProps } from "../../DragAndDrop/DragAndDropContext";
import { Droppable } from "../../DragAndDrop/Droppable";
import { useForkRef } from "../../../hooks/useForkRef";
import { Draggable } from "../../DragAndDrop/Draggable";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";
import { IDragHandleProps } from "../../DragAndDrop/DragAndDropTypes";

export const ControlConstArray = (model: TGenericModelNode): IInspectorControls => {
	return {
		headerControls: {
			component: ControlConstArray_HeaderControls,
			model: model
		},
		children: {
			component: ControlConstArray_Children,
			model: model
		}
	};
};

export const ControlConstArray_HeaderControls: TInspectorControlComponent<
	ISchemaConstArrayModel<TGenericBlueprintSchema>
> = ({ model: _model }) => {
	const { model } = useBlueprintModel(_model, "constArray");

	const inspectorContext = useEditorInspectorContext();

	const addItem = useCallback(() => {
		if (!model) {
			return;
		}

		model.schema.addElement(model, undefined, true);
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return <FieldHeaderButton icon="mdi/plus" onClick={addItem} disabled={inspectorContext?.readOnly} />;
};

export const ControlConstArray_Children: TInspectorControlComponent<
	ISchemaConstArrayModel<TGenericBlueprintSchema>
> = ({ model: _model, path }) => {
	const { model } = useBlueprintModel(_model, "constArray");

	const inspectorContext = useEditorInspectorContext();

	const handleAdd = useCallback(() => {
		if (!model) {
			return;
		}

		model.schema.addElement(model, undefined, true);
	}, [ model ]);

	const handleRemove = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.removeElement(model, index, true);
		},
		[ model ]
	);

	const handleDragEnd = useCallback<IDragAndDropContextProps["onDragEnd"]>(
		({ destination, source }) => {
			if (!destination) {
				return;
			}

			model.schema.moveElement(model, source.index, destination.index, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			{model.items.length > 0 ? (
				<DragAndDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId={model.schema._id}>
						{({ droppableRef, droppableProps, placeholder }) => (
							<FieldRow type={FIELD_ROW_TYPE.LIST} ref={droppableRef} {...droppableProps}>
								{model.items.map((item, index) => (
									<Draggable
										disabled={inspectorContext?.readOnly}
										draggableId={String(item.nodeId)}
										key={item.nodeId}
										index={index}
									>
										{({ draggableRef, draggableProps, dragHandleProps }) => (
											<ControlConstArray_ChildItem
												key={item.nodeId}
												rootSchema={model.schema}
												model={item}
												path={path.concat([ "item" ])}
												index={index}
												onRemove={handleRemove}
												ref={draggableRef}
												dragHandleProps={dragHandleProps}
												{...draggableProps}
											/>
										)}
									</Draggable>
								))}
								{placeholder}
							</FieldRow>
						)}
					</Droppable>
				</DragAndDropContext>
			) : (
				<FieldRow type={FIELD_ROW_TYPE.LIST}>
					<FieldListItem
						label="Add item"
						onClick={handleAdd}
						align={FIELD_LIST_ITEM_ALIGN.CENTER}
						disabled={inspectorContext?.readOnly}
					/>
				</FieldRow>
			)}
		</>
	);
};

interface IControlConstArray_ChildItemProps extends IFieldListItemProps {
	rootSchema: ISchemaConstArray<TGenericBlueprintSchema>;
	model: TGenericModelNode;
	path: string[];
	index: number;
	onRemove: (index: number) => void;
	dragHandleProps?: IDragHandleProps;
}

const ControlConstArray_ChildItem = React.forwardRef<HTMLDivElement, IControlConstArray_ChildItemProps>(
	({ rootSchema, model: _model, index, path, onRemove, dragHandleProps, ...props }, ref) => {
		const { model } = useBlueprintModel(_model);

		const inspectorContext = useEditorInspectorContext();

		const [ isPanelOpened, setIsPanelOpened ] = useState(false);
		const itemElRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(itemElRef, ref);

		const itemClicked = useRef(false);

		const itemActions = useMemo(
			() => [
				{
					icon: "mdi/delete",
					tooltip: "Remove item",
					onClick: () => {
						if (isPanelOpened) {
							setIsPanelOpened(false);
							setTimeout(() => onRemove(index), 200);
						} else {
							onRemove(index);
						}
					}
				} as IFieldListItemAction
			],
			[ index, isPanelOpened ]
		);

		const renderPanel = useCallback(() => {
			return <InspectModel model={_model} depth={0} path={path} />;
		}, [ _model ]);

		const togglePanel = useCallback(() => {
			setIsPanelOpened((x) => !x);
			itemClicked.current = true;
			setTimeout(() => (itemClicked.current = false), 1);
		}, []);

		const closePanel = useCallback(() => {
			setIsPanelOpened(false);
		}, [ setIsPanelOpened ]);

		const label =
			(typeof rootSchema.opts.getElementModelNodeInfo === "function" &&
				rootSchema.opts.getElementModelNodeInfo(model, index)?.label) ||
			"#" + (index + 1);

		return (
			<FieldListItem
				label={label}
				actions={itemActions}
				isActive={isPanelOpened}
				onClick={togglePanel}
				ref={forkRef}
				dragHandleProps={dragHandleProps}
				{...props}
				readOnly={inspectorContext?.readOnly}
			>
				<InspectorFloatingPanel
					for={itemElRef}
					isOpened={isPanelOpened}
					render={renderPanel}
					onClose={closePanel}
				/>
			</FieldListItem>
		);
	}
);
